import { FC, useEffect, useRef } from "react";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  toast,
} from "@capacit-com/lara-law-shared-ui";
import { Building2, User, Share2, Copy } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Dashboard } from "@/models/dashboard/Dashboard";
import { useCloneDashboardAsNew } from "@/hooks/useDashboard";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import { Permission } from "@/lib/permissions/Permission.enum";
import { logger } from "@/lib/logger";
import useDashboardStore from "@/stores/dashboard.store";
import useTenant from "@/hooks/useTenant";
import { cn } from "@/lib/utils";
import {
  getDashboardIdentity,
  groupDashboardsByAudience,
  parentTenantDisplayName,
} from "../lib/dashboardIdentity";
import { findOrganisationNameClash } from "../lib/dashboardNameClash";
import { DashboardIdentityPill } from "./DashboardIdentityPill";
import DashboardAudienceChoice from "./DashboardAudienceChoice";
import DashboardNameClashWarning, {
  NAME_CLASH_INPUT_CLASS,
} from "./DashboardNameClashWarning";

const formSchema = z.object({
  sourceId: z.string().min(1, { message: "Vælg et dashboard" }),
  cloneName: z.string().trim().min(1, { message: "Udfyld navn" }),
  isOrganizationDashboard: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

const suggestedCloneName = (source: Dashboard | undefined) =>
  source ? `${source.name} (Kopi)` : "";

interface CloneDashboardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboards: Dashboard[];
  sourceDashboardId: string | null;
}

const CloneDashboardDialog: FC<CloneDashboardDialogProps> = ({
  open,
  onOpenChange,
  dashboards,
  sourceDashboardId,
}) => {
  const cloneDashboardAsNew = useCloneDashboardAsNew();
  const hasPermission = useHasPermission();
  const { setSelectedDashboard, setLocalConfig } = useDashboardStore();
  const { useQueryCurrent } = useTenant();
  const { data: currentTenant } = useQueryCurrent();

  const canCreateOrgDashboard = hasPermission({
    allOf: [Permission.UpdateDashboardLayout],
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sourceId: "",
      cloneName: "",
      isOrganizationDashboard: false,
    },
  });

  // Pre-fill when the dialog opens, and only then. Every refetch of the list (a refocus, another
  // administrator's save) hands over a new `dashboards` array, which would otherwise wipe the name
  // being typed.
  const wasOpen = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpen.current;
    wasOpen.current = open;
    if (!justOpened) return;
    const initialId = sourceDashboardId ?? dashboards[0]?.id ?? "";
    form.reset({
      sourceId: initialId,
      cloneName: suggestedCloneName(dashboards.find((d) => d.id === initialId)),
      isOrganizationDashboard: false,
    });
  }, [open, sourceDashboardId, dashboards, form]);

  const selectedSourceId = form.watch("sourceId");
  const cloneName = form.watch("cloneName");
  const isOrganizationDashboard = form.watch("isOrganizationDashboard");
  const sourceDashboard = dashboards.find((d) => d.id === selectedSourceId);
  const nameClash = findOrganisationNameClash(cloneName, dashboards);

  // Update the suggested name when the source changes
  const handleSourceChange = (id: string) => {
    form.setValue("sourceId", id, { shouldDirty: true });
    form.setValue(
      "cloneName",
      suggestedCloneName(dashboards.find((d) => d.id === id)),
      { shouldDirty: true }
    );
  };

  const handleCreate = form.handleSubmit(async (values) => {
    try {
      const cloned = await cloneDashboardAsNew.mutateAsync({
        id: values.sourceId,
        request: {
          name: values.cloneName,
          isOrganizationDashboard:
            canCreateOrgDashboard && values.isOrganizationDashboard,
        },
      });
      setSelectedDashboard(cloned.id);
      setLocalConfig(cloned.config);
      onOpenChange(false);
      toast.success("Dashboard klonet", {
        description: `"${cloned.name}" er oprettet som et nyt dashboard.`,
      });
    } catch (e) {
      logger.error("Error cloning dashboard", {
        operation: "cloneDashboardAsNew",
        error: e,
        sourceDashboardId: values.sourceId,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved kloning af dashboard.",
      });
    }
  });

  const getDashboardIcon = (dashboard: Dashboard) => {
    if (dashboard.isFromParentTenant)
      return <Share2 className="h-4 w-4 text-primary" />;
    if (dashboard.isOrganizationDashboard)
      return <Building2 className="h-4 w-4" />;
    return <User className="h-4 w-4" />;
  };

  const {
    parent: parentDashboards,
    organisation: orgDashboards,
    personal: personalDashboards,
    parentTenantName,
  } = groupDashboardsByAudience(dashboards);

  const renderSourceItem = (d: Dashboard) => (
    <SelectItem key={d.id} value={d.id}>
      <div className="flex items-center gap-2">
        <span>{d.name}</span>
        <DashboardIdentityPill identity={getDashboardIdentity(d)} size="row" />
      </div>
    </SelectItem>
  );

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      isDirty={form.formState.isDirty}
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Copy className="h-5 w-5" />
          Klon dashboard
        </DialogTitle>
        <DialogDescription>
          Vælg et dashboard at kopiere og giv kopien et nyt navn.
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={handleCreate}>
          <div className="py-4 flex flex-col gap-4">
            {/* Source selector */}
            <FormField
              control={form.control}
              name="sourceId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Klon fra</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={handleSourceChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Vælg dashboard">
                          {sourceDashboard && (
                            <div className="flex items-center gap-2">
                              {getDashboardIcon(sourceDashboard)}
                              <span>{sourceDashboard.name}</span>
                            </div>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {parentDashboards.length > 0 && (
                        <>
                          <SelectGroup>
                            <SelectLabel className="flex items-center gap-2">
                              <Share2 className="h-4 w-4 text-primary" />
                              Fra {parentTenantDisplayName(parentTenantName)}
                            </SelectLabel>
                            {parentDashboards.map(renderSourceItem)}
                          </SelectGroup>
                          <SelectSeparator />
                        </>
                      )}

                      {orgDashboards.length > 0 && (
                        <>
                          <SelectGroup>
                            <SelectLabel className="flex items-center gap-2">
                              <Building2 className="h-4 w-4" />
                              Organisation
                            </SelectLabel>
                            {orgDashboards.map(renderSourceItem)}
                          </SelectGroup>
                          <SelectSeparator />
                        </>
                      )}

                      {personalDashboards.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Personligt
                          </SelectLabel>
                          {personalDashboards.map(renderSourceItem)}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Clone name */}
            <FormField
              control={form.control}
              name="cloneName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Navn på nyt dashboard</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Mit dashboard (Kopi)"
                      className={cn(nameClash && NAME_CLASH_INPUT_CLASS)}
                    />
                  </FormControl>
                  <FormMessage />
                  <DashboardNameClashWarning
                    clash={nameClash}
                    isOrganizationDashboard={
                      canCreateOrgDashboard && isOrganizationDashboard
                    }
                  />
                </FormItem>
              )}
            />

            {canCreateOrgDashboard && (
              <DashboardAudienceChoice
                idPrefix="clone-dashboard"
                isOrganizationDashboard={isOrganizationDashboard}
                onChange={(value) =>
                  form.setValue("isOrganizationDashboard", value, {
                    shouldDirty: true,
                  })
                }
                tenantName={currentTenant?.name}
              />
            )}
          </div>

          <DialogFooter>
            <FormDialogCancel />
            <Button
              type="submit"
              disabled={
                !selectedSourceId ||
                !cloneName.trim() ||
                cloneDashboardAsNew.isPending
              }
            >
              <Copy className="h-4 w-4 mr-2" />
              {nameClash ? "Klon alligevel" : "Opret klon"}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default CloneDashboardDialog;
